import type { Collector } from "./types";
import { isBrowser } from "./types";

export interface BotSignals {
  webdriver: boolean;
  outerSizeZero: boolean;
  automationProps: string[];
  pluginCount: number;
  chromeRuntime: boolean | null;
  languagesEmpty: boolean;
  screenZero: boolean;
  hasFocus: boolean | null;
}

export const botCollector: Collector<BotSignals> = {
  name: "bot",
  version: 1,
  timeoutMs: 200,
  collect() {
    return collectBotSignals();
  },
};

/** Bot / automation signals — parallel track; not used in device_core_id. */
function collectBotSignals(): BotSignals {
  if (!isBrowser) {
    return {
      webdriver: false,
      outerSizeZero: false,
      automationProps: [],
      pluginCount: 0,
      chromeRuntime: null,
      languagesEmpty: false,
      screenZero: false,
      hasFocus: null,
    };
  }

  const w = window as any;
  const doc = document as any;
  const automationProps: string[] = [];
  if (w.__playwright !== undefined) automationProps.push("__playwright");
  if (w.__pw_manual !== undefined) automationProps.push("__pw_manual");
  if (w.domAutomation !== undefined) automationProps.push("domAutomation");
  if (w.domAutomationController !== undefined) automationProps.push("domAutomationController");
  try {
    for (const key of Object.getOwnPropertyNames(doc)) {
      if (key.startsWith("$cdc_") || key.startsWith("__$webdriver")) {
        automationProps.push(key);
      }
    }
  } catch {
    /* ignore */
  }
  if (w.callPhantom !== undefined) automationProps.push("callPhantom");
  if (w._phantom !== undefined) automationProps.push("_phantom");
  if (w.__nightmare !== undefined) automationProps.push("__nightmare");

  const ua = navigator.userAgent || "";
  const claimsChromium =
    /\b(Chrome|Chromium|Edg|OPR|Opera)\b/i.test(ua) ||
    /\bCriOS\b/i.test(ua) ||
    /\bwv\b.*\bVersion\b/i.test(ua);
  const claimsFirefox = /\bFirefox\b/i.test(ua) && !/\bSeamonkey\b/i.test(ua);
  const claimsSafariOnly =
    /\bSafari\b/i.test(ua) && !/\b(Chrome|Chromium|CriOS|Edg|OPR)\b/i.test(ua);

  let chromeRuntime: boolean | null = null;
  if (claimsChromium && !claimsSafariOnly) {
    try {
      chromeRuntime =
        typeof w.chrome !== "undefined" &&
        w.chrome != null &&
        typeof w.chrome.runtime !== "undefined";
    } catch {
      chromeRuntime = false;
    }
  } else if (claimsFirefox || claimsSafariOnly) {
    chromeRuntime = null;
  } else {
    chromeRuntime = null;
  }

  const outerW = typeof w.outerWidth === "number" ? w.outerWidth : 0;
  const outerH = typeof w.outerHeight === "number" ? w.outerHeight : 0;
  const outerSizeZero = outerW === 0 && outerH === 0;

  let hasFocus: boolean | null = null;
  try {
    if (w === w.top) {
      hasFocus = document.hasFocus();
    }
  } catch {
    hasFocus = null;
  }

  return {
    webdriver: !!(navigator as any).webdriver,
    outerSizeZero,
    automationProps: [...new Set(automationProps)].sort(),
    pluginCount: typeof navigator.plugins !== "undefined" ? navigator.plugins.length : 0,
    chromeRuntime,
    languagesEmpty: (navigator.languages?.length ?? 0) === 0,
    screenZero: screen.width === 0 || screen.height === 0,
    hasFocus,
  };
}
